import React, { useContext, useRef } from 'react';
import { useQuery } from '@apollo/client';
import styles from 'styles/partials/_navbar.css';
import Async from 'react-select/async';
import OmniMenuList from './OmniMenuList';
import debounce from 'es6-promise-debounce';
import listQuery from '../../graphql/queries/OmniListQuery.graphql';
import * as entities from '../../graphql/entities';
import { forceReload } from '../connectedForm/utils/actions';
import { Badge } from '../connectedForm/Tab';
import UserContext from '../Context/UserContext';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { GenericArrayCard, GenericArrayCardSubtitle, GenericArrayCardTitle } from '../connectedForm/GenericArray';

const entityMap = {
  customers: entities.Customer,
  suppliers: entities.Supplier,
  products: entities.Product,
  salesOrders: entities.CombinedSales,
  salesInvoices: entities.SalesInvoice,
  purchaseOrders: entities.PurchaseOrder,
  purchaseInvoices: entities.PurchaseInvoice,
  nominals: entities.Nominal,
  events: entities.DiaryEvent,
  salesCredits: entities.SalesCredit,
  proformaInvoices: {
    ...entities.ProformaInvoice,
    formUrl: '/Sales/Order/Details/',
    workspaceUrl: '/Sales/ProformaInvoices/Form/',
  },
};

const OmniSearchBar = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { currentUser } = useContext(UserContext);
  const valueRef = useRef('');
  const { refetch } = useQuery(listQuery, { skip: true });

  const loadOptions = debounce((value) => {
    if (value === '?') {
      valueRef.current = '';
    } else {
      valueRef.current = value;
    }

    return refetch({
      searchTerm: valueRef.current,
      limit: 5, // per section
    }).then((response) => {
      return response.data.omniSearch.map((omniSearchResultGroup) => {
        const { entityName, results, count } = omniSearchResultGroup;
        return {
          key: entityName,
          label: entityMap[entityName].title,
          count: count,
          genericCount: count,
          options: !results
            ? []
            : results.map((item) => {
                return {
                  key: entityName,
                  label: item,
                  value: item,
                  menuModule: entityName,
                };
              }),
        };
      });
    });
  }, 200);

  const handleChange = (value?: { key: string; value: { id: number } }) => {
    if (!value || !history || !entityMap[value.key]) return;
    const newPathname = `${entityMap[value.key].formUrl}${value.value.id}`;
    if (newPathname === history.location?.pathname) return;
    history.push({
      pathname: newPathname,
      state: {
        previousLocation: history.location?.pathname || 'back',
      },
    });
    //Force reload - Otherwise navigating to other records for the same entity will not function (e.g. 'Changing to customer with id '5' from customer with id '3').
    dispatch(forceReload(entityMap[value.key].type));
  };

  const handleGroupClick = (key: string) => {
    history.push({
      pathname: `${entityMap[key].workspaceUrl}`,
      state: { genericFilter: valueRef.current, previousLocation: history.location?.pathname || 'back' },
    });
  };

  const formatGroupLabel = (data: { key: string; label: string; genericCount: number }) => {
    return (
      <div onClick={() => handleGroupClick(data.key)}>
        {data.label} <Badge>{`${data.genericCount}`}</Badge>
      </div>
    );
  };

  const formatOptionLabel = ({ value, label, key }: { value: any; label: string; key: string }, { context }) => {
    if (context !== 'menu') {
      //Only want to display the cards in the menu. Does not fit into the control section.
      return <div style={{ display: 'flex' }}>{label}</div>;
    }

    if (
      [
        'customers',
        'suppliers',
        'products',
        'salesOrders',
        'salesInvoices',
        'purchaseInvoices',
        'purchaseOrders',
        'nominals',
        'events',
        'salesCredits',
        'proformaInvoices',
      ].indexOf(key) === -1
    ) {
      return <div style={{ display: 'flex' }}>{label}</div>;
    } else {
      return (
        <GenericArrayCard>
          <GenericArrayCardTitle>{value.headerDisplayValue}</GenericArrayCardTitle>
          <GenericArrayCardSubtitle>{value.subHeaderDisplayValue}</GenericArrayCardSubtitle>
        </GenericArrayCard>
      );
    }
  };

  if (!currentUser.currentCompany) return null;
  return (
    <>
      <div className={styles.searchPlaceHolder}>
        <Async
          components={{
            MenuList: OmniMenuList,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
          }}
          isMulti={false}
          onChange={handleChange}
          loadOptions={loadOptions}
          placeholder={
            <div>
              <div className={styles.searchIcon}>
                <i className="icon">search</i>
              </div>
              <div className={styles.searchPlaceholderText}>Search</div>
            </div>
          }
          styles={{
            control: (provided) => ({
              ...provided,
              background: '#45439c',
              fontWeight: '500',
              border: 'none',
              minHeight: '42px',
            }),
            menu: (provided) => {
              return {
                ...provided,
                width: '1300px',
                marginLeft: '-425px',
                zIndex: 1000001, //Explicitly higher than other content - If you can access the bar to search, should be on top.
              };
            },
            placeholder: (provided) => ({
              ...provided,
              fontSize: '14px',
              fontWeight: '500',
              color: p4Tokens.colorNeutralForegroundStaticInverted,
            }),
            menuList: (provided) => {
              return {
                ...provided,
                maxHeight: '800px',
              };
            },
            option: (provided, state) => {
              return {
                ...provided,
                backgroundColor: state.isFocused
                  ? p4Tokens.colorNeutralBackground1Hover
                  : p4Tokens.colorNeutralBackground1,
                borderLeft: state.isFocused ? '3px solid #00b9af' : '3px solid transparent',
              };
            },
          }}
          theme={(theme) => ({
            ...theme,
            colors: {
              ...theme.colors,
              text: p4Tokens.colorNeutralForegroundStaticInverted,
              neutral0: p4Tokens.colorNeutralBackground1, //Menu Background
              neutral40: p4Tokens.colorNeutralForeground4, //Menu Text
              neutral50: p4Tokens.colorNeutralForegroundStaticInverted, //Placeholder
              neutral80: p4Tokens.colorNeutralForegroundStaticInverted, //Input,
              primary: '#846ec1',
              primary25: '#907cc7',
            },
          })}
          formatGroupLabel={formatGroupLabel}
          formatOptionLabel={formatOptionLabel}
          value={null}
          noOptionsMessage={({ inputValue }) => {
            if (inputValue === '') {
              return 'Start typing to begin search...';
            }
            return 'No Options';
          }}
        />
      </div>
    </>
  );
};

export default OmniSearchBar;
